//
// Copyright © 2026 Hardcore Engineering Inc.
//
// Licensed under the Eclipse Public License, Version 2.0 (the "License");
// you may not use this file except in compliance with the License. You may
// obtain a copy of the License at https://www.eclipse.org/legal/epl-2.0
//
// Unless required by applicable law or agreed to in writing, software
// distributed under the License is distributed on an "AS IS" BASIS,
// WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
//
// See the License for the specific language governing permissions and
// limitations under the License.
//

/**
 * What a money record must look like, in one place: the dialogs build their
 * records with these functions, and the server refuses a write that fails the
 * same checks (KpiPermissionsMiddleware). So no screen, script or API call
 * can put a figure in the books that the payroll engine would misread: a
 * negative or fractional-tiyin amount, an expense filed under an income
 * category, a payment for month 13.
 */

import { type Employee } from '@hcengineering/contact'
import { type Ref } from '@hcengineering/core'
import { round2 } from './calc'
import { isPaidComponent, isPayComponent } from './payroll'
import {
  type Compensation,
  type FinanceAccount,
  type FxRate,
  type MyReceipt,
  type PayComponent,
  type QuarterScore,
  type SalaryPeriod,
  type SpotBonus,
  type Transaction,
  type TransactionCategory,
  type TransactionKind
} from './types'

/** Amounts above this are a typing accident, and lose tiyin as floats. */
export const MAX_AMOUNT = 1e13

/** A finite amount to the tiyin / cent (no third decimal), within range. */
export function isMoney (value: unknown, opt: { zero?: boolean, negative?: boolean } = {}): value is number {
  if (typeof value !== 'number' || !Number.isFinite(value) || Math.abs(value) >= MAX_AMOUNT) return false
  if (value === 0) return opt.zero === true
  if (value < 0 && opt.negative !== true) return false
  return round2(value) === value
}

export function isYear (value: unknown): value is number {
  return typeof value === 'number' && Number.isInteger(value) && value >= 2000 && value <= 2100
}

export function isMonth (value: unknown): value is number {
  return typeof value === 'number' && Number.isInteger(value) && value >= 1 && value <= 12
}

function isTimestamp (value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value) && value > 0
}

// ---- ledger entries ----------------------------------------------------------

/** What the add / edit transaction dialog collects. */
export interface LedgerEntryForm {
  kind: TransactionKind
  amountUzs: number
  occurredAt: number
  description?: string | null
  account: Ref<FinanceAccount>
  /** Every entry names a category (decision 11). */
  category: Ref<TransactionCategory>
  employee?: Ref<Employee> | null
  /** The month the payment is for. Kept on an employee's entries only. */
  payYear: number
  payMonth: number
  /** Soum per USD the pay month is converted at. Kept on pay entries only. */
  rate: number
}

export type LedgerEntryData = Pick<
Transaction,
'kind' | 'amountUzs' | 'occurredAt' | 'description' | 'account' | 'category' | 'employee' | 'payYear' | 'payMonth' | 'fxRate'
>

/**
 * Payroll rule 1: an entry counts toward someone's pay when it names the
 * employee AND its category settles an owed component (salary, the quarter
 * bonus or premiya; never holiday money).
 */
export function isPayrollEntry (
  entry: { employee?: Ref<Employee> | null, category?: Ref<TransactionCategory> | null },
  categories: TransactionCategory[]
): boolean {
  if (entry.employee == null || entry.category == null) return false
  return categories.some((c) => c._id === entry.category && isPaidComponent(c.component))
}

/** Whether an entry is one of a person's months at all: pay, or holiday money given to them. */
export function isEmployeeEntry (
  entry: { employee?: Ref<Employee> | null, category?: Ref<TransactionCategory> | null },
  categories: TransactionCategory[]
): boolean {
  if (entry.employee == null || entry.category == null) return false
  return categories.some((c) => c._id === entry.category && isPayComponent(c.component))
}

/**
 * The record a ledger dialog writes. The amount is positive and to the tiyin;
 * anything left empty is null (an update drops undefined, so a removed
 * employee would otherwise come back on reload); the pay month is kept on an
 * employee's entries (pay and holiday money, so both sit in the right month)
 * and the rate on pay entries only, so nothing else is ever counted as pay.
 */
export function ledgerEntryData (form: LedgerEntryForm, categories: TransactionCategory[]): LedgerEntryData {
  const payroll = isPayrollEntry(form, categories)
  const ofEmployee = isEmployeeEntry(form, categories)
  const description = (form.description ?? '').trim()
  return {
    kind: form.kind,
    amountUzs: round2(Math.abs(form.amountUzs)),
    occurredAt: form.occurredAt,
    description: description !== '' ? description : null,
    account: form.account,
    category: form.category,
    employee: form.employee ?? null,
    payYear: ofEmployee ? form.payYear : null,
    payMonth: ofEmployee ? form.payMonth : null,
    fxRate: payroll ? form.rate : null
  }
}

/**
 * Why a ledger entry must not be saved, or undefined when it is sound.
 * `category` is the entry's category document (undefined when it names one
 * that does not exist); `accountExists` likewise for its account.
 */
export function ledgerEntryProblem (
  entry: Partial<Transaction>,
  refs: { category: TransactionCategory | undefined, accountExists: boolean }
): string | undefined {
  if (entry.kind !== 'income' && entry.kind !== 'expense') return 'kind'
  if (!isMoney(entry.amountUzs)) return 'amount'
  if (!isTimestamp(entry.occurredAt)) return 'date'
  if (typeof entry.account !== 'string' || !refs.accountExists) return 'account'
  // Every entry names a category that exists (decision 11).
  if (entry.category == null || refs.category === undefined) return 'category'
  // A category belongs to one side of the ledger.
  if (refs.category.kind !== entry.kind) return 'category-kind'
  if (entry.employee != null && typeof entry.employee !== 'string') return 'employee'
  const hasYear = entry.payYear != null
  const hasMonth = entry.payMonth != null
  if (hasYear !== hasMonth) return 'pay-month'
  if (hasYear && (!isYear(entry.payYear) || !isMonth(entry.payMonth))) return 'pay-month'
  if (entry.fxRate != null && !(typeof entry.fxRate === 'number' && Number.isFinite(entry.fxRate) && entry.fxRate > 0)) {
    return 'rate'
  }
  return undefined
}

// ---- receipts ----------------------------------------------------------------

/**
 * The fields an employee authors on their receipt; everything else is the
 * server's. `component` is what they say the transfer was (RECEIPT_KINDS).
 */
export const RECEIPT_AUTHORED_FIELDS = ['amountUzs', 'file', 'fileName', 'note', 'component'] as const

/**
 * What an employee may say a transfer was (owner, 2026-10-14): salary or
 * holiday money. The quarter bonus and premiya never come by receipt -- they
 * are booked by the admins -- though a reviewer may still file a receipt so.
 */
export const RECEIPT_KINDS: readonly PayComponent[] = ['salary', 'holiday']

/**
 * Why an employee's receipt must not be saved. A receipt is for a month that
 * has started, or the next one (money paid ahead, owner 2026-10-14):
 * `currentMonthIdx` is the current business month (payroll.ts businessMonthIdx).
 */
export function receiptProblem (
  receipt: Partial<Pick<MyReceipt, 'amountUzs' | 'year' | 'month' | 'file' | 'fileName' | 'component'>>,
  currentMonthIdx: number
): string | undefined {
  if (!isMoney(receipt.amountUzs)) return 'amount'
  if (receipt.component != null && !RECEIPT_KINDS.includes(receipt.component)) return 'component'
  if (!isYear(receipt.year) || !isMonth(receipt.month)) return 'period'
  if (receipt.year * 12 + (receipt.month - 1) > currentMonthIdx + 1) return 'period-future'
  if (typeof receipt.file !== 'string' || receipt.file === '') return 'file'
  if (typeof receipt.fileName !== 'string') return 'file'
  return undefined
}

// ---- salary records, scores, bonuses, the rate ----------------------------------

function isSalaryPeriod (p: SalaryPeriod): boolean {
  return (
    p != null &&
    isYear(p.year) &&
    isMonth(p.month) &&
    isMoney(p.baseSalaryUsd, { zero: true }) &&
    isPercent(p.bonusPct)
  )
}

function isPercent (value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value) && value >= 0 && value <= 100
}

export function compensationProblem (comp: Partial<Compensation>): string | undefined {
  if (typeof comp.employee !== 'string' || comp.employee === '') return 'employee'
  if (!isMoney(comp.baseSalaryUsd, { zero: true })) return 'salary'
  if (!isPercent(comp.bonusPct)) return 'bonus'
  if (!isTimestamp(comp.effectiveFrom)) return 'pay-start'
  if (comp.effectiveTo != null) {
    if (!isTimestamp(comp.effectiveTo)) return 'pay-end'
    // Compared by month where it matters (the engine); a pay end before the
    // pay start by date is wrong in any case.
    if (comp.effectiveTo < comp.effectiveFrom && monthOf(comp.effectiveTo) < monthOf(comp.effectiveFrom)) return 'pay-end'
  }
  if (comp.salaryHistory != null) {
    if (!Array.isArray(comp.salaryHistory) || !comp.salaryHistory.every(isSalaryPeriod)) return 'history'
  }
  return undefined
}

/** Business (Tashkent) month index of a timestamp; local copy to avoid a cycle with payroll.ts. */
function monthOf (ts: number): number {
  const d = new Date(ts + 5 * 60 * 60 * 1000)
  return d.getUTCFullYear() * 12 + d.getUTCMonth()
}

export function quarterScoreProblem (score: Partial<QuarterScore>): string | undefined {
  if (typeof score.employee !== 'string' || score.employee === '') return 'employee'
  if (!isYear(score.year)) return 'period'
  if (!(typeof score.quarter === 'number' && Number.isInteger(score.quarter) && score.quarter >= 1 && score.quarter <= 4)) {
    return 'period'
  }
  if (typeof score.kpi !== 'number' || !Number.isFinite(score.kpi)) return 'kpi'
  return undefined
}

export function spotBonusProblem (spot: Partial<SpotBonus>): string | undefined {
  if (typeof spot.employee !== 'string' || spot.employee === '') return 'employee'
  if (!isYear(spot.year) || !isMonth(spot.month)) return 'period'
  // 0 is how a mistaken bonus is withdrawn (history is never deleted).
  if (!isMoney(spot.amountUzs, { zero: true })) return 'amount'
  return undefined
}

/**
 * Why a category must not be saved: a name, a side of the ledger, and a
 * component that exists. Holiday money is given, so its category is an
 * expense.
 */
export function categoryProblem (category: Partial<TransactionCategory>): string | undefined {
  if (typeof category.name !== 'string' || category.name.trim() === '') return 'name'
  if (category.kind !== 'income' && category.kind !== 'expense') return 'kind'
  if (category.component != null && !isPayComponent(category.component)) return 'component'
  if (category.component === 'holiday' && category.kind !== 'expense') return 'component-kind'
  return undefined
}

export function fxRateProblem (rate: Partial<FxRate>): string | undefined {
  if (!(typeof rate.usdToUzs === 'number' && Number.isFinite(rate.usdToUzs) && rate.usdToUzs > 0 && rate.usdToUzs < MAX_AMOUNT)) {
    return 'rate'
  }
  return undefined
}

export function financeAccountProblem (account: Partial<FinanceAccount>): string | undefined {
  if (typeof account.name !== 'string' || account.name.trim() === '') return 'name'
  if (!isMoney(account.openingBalanceUzs, { zero: true, negative: true })) return 'amount'
  return undefined
}
